import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import { supabase } from '../lib/supabase';

interface UserData {
  firstName: string;
  lastName: string;
}

const GoogleCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'collecting-data' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string>('');
  const [userData, setUserData] = useState<UserData>({ firstName: '', lastName: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleGoogleCallback = async () => {
      try {
        console.log('=== Google OAuth Callback ===');
        
        // Get the creator_id from URL params
        const creatorId = searchParams.get('creator_id');
        console.log('Creator ID:', creatorId);

        // Get the current session after OAuth redirect
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('Session error:', sessionError);
          setStatus('error');
          setError('Failed to complete Google sign-up. Please try again.');
          return;
        }

        if (!session) {
          console.error('No session found');
          setStatus('error');
          setError('Authentication failed. Please try again.');
          return;
        }

        console.log('Session obtained:', session.user.email);
        console.log('User metadata:', session.user.user_metadata);

        // Check if user already exists (has email_confirmed_at)
        if (session.user.email_confirmed_at) {
          console.log('User already exists and is confirmed');
          setStatus('error');
          setError('An account with this email already exists. Please sign in with your password instead.');
          return;
        }

        // Extract user data from Google profile
        const googleData = session.user.user_metadata;
        const firstName = googleData.full_name?.split(' ')[0] || googleData.first_name || '';
        const lastName = googleData.full_name?.split(' ').slice(1).join(' ') || googleData.last_name || '';

        console.log('Extracted user data:', { firstName, lastName });

        // If we have both first and last name, proceed with account creation
        if (firstName && lastName) {
          await createUserAccount(session.user, firstName, lastName, creatorId);
        } else {
          // Need to collect missing data
          setUserData({ firstName, lastName });
          setStatus('collecting-data');
        }

      } catch (err) {
        console.error('Unexpected error during Google callback:', err);
        setStatus('error');
        setError('An unexpected error occurred. Please try again.');
      }
    };

    handleGoogleCallback();
  }, [searchParams, navigate]);

  const createUserAccount = async (user: any, firstName: string, lastName: string, creatorId: string | null) => {
    try {
      console.log('Creating user account with data:', { firstName, lastName, creatorId });

      // Update user metadata with required fields
      const { error: updateError } = await supabase.auth.updateUser({
        data: {
          user_type: 'subscriber',
          creator_id: creatorId,
          first_name: firstName,
          last_name: lastName,
          full_name: `${firstName} ${lastName}`
        }
      });

      if (updateError) {
        console.error('Error updating user metadata:', updateError);
        setStatus('error');
        setError('Failed to create account. Please try again.');
        return;
      }

      console.log('User account created successfully');
      setStatus('success');

      // Redirect to success page
      setTimeout(() => {
        navigate(`/${creatorId}/success`, { 
          state: { 
            email: user.email,
            from: '/'
          } 
        });
      }, 2000);

    } catch (err) {
      console.error('Error creating user account:', err);
      setStatus('error');
      setError('Failed to create account. Please try again.');
    }
  };

  const handleDataSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!userData.firstName.trim() || !userData.lastName.trim()) {
      setError('First name and last name are required.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const creatorId = searchParams.get('creator_id');

      if (session) {
        await createUserAccount(session.user, userData.firstName, userData.lastName, creatorId);
      } else {
        setStatus('error');
        setError('Session expired. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting user data:', err);
      setError('Failed to create account. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUserData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (status === 'collecting-data') {
    return (
      <div className='verification-container'>
        <div className='verification-content'>
          <div className='verification-card'>
            <div className='verification-icon'>
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="32" cy="32" r="32" fill="#177508"/>
                <path d="M32 16L36 24L44 24L38 30L40 38L32 34L24 38L26 30L20 24L28 24L32 16Z" fill="white"/>
              </svg>
            </div>
            
            <h1 className='titles large color black'>Complete Your Profile</h1>
            
            <p className='paragraphs medium color charcoal'>
              Please provide your first and last name to complete your account setup.
            </p>

            <form onSubmit={handleDataSubmit} style={{ marginTop: '24px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label>
                  <span className='labels large color black'>First name <span className='required'>*</span></span>
                  <input
                    type="text"
                    name="firstName"
                    value={userData.firstName}
                    onChange={handleInputChange}
                    required
                    placeholder='Your first name'
                    className='paragraphs medium color black'
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #E0E0E0',
                      borderRadius: '8px',
                      marginTop: '4px'
                    }}
                  />
                </label>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label>
                  <span className='labels large color black'>Last name <span className='required'>*</span></span>
                  <input
                    type="text"
                    name="lastName"
                    value={userData.lastName}
                    onChange={handleInputChange}
                    required
                    placeholder='Your last name'
                    className='paragraphs medium color black'
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #E0E0E0',
                      borderRadius: '8px',
                      marginTop: '4px'
                    }}
                  />
                </label>
              </div>

              {error && (
                <div className="error-message general-error" style={{ marginBottom: '16px' }}>
                  {error}
                </div>
              )}

              <button 
                type="submit" 
                className="btn primary"
                disabled={isSubmitting}
                style={{ width: '100%' }}
              >
                {isSubmitting ? 'Creating Account...' : 'Complete Sign Up'}
              </button>
            </form>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  const getStatusContent = () => {
    switch (status) {
      case 'loading':
        return {
          icon: (
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="32" cy="32" r="32" fill="#177508"/>
              <path d="M32 8C18.745 8 8 18.745 8 32s10.745 24 24 24 24-10.745 24-24S45.255 8 32 8zm0 44c-11.046 0-20-8.954-20-20s8.954-20 20-20 20 8.954 20 20-8.954 20-20 20z" fill="white"/>
              <path d="M32 16c-8.837 0-16 7.163-16 16s7.163 16 16 16 16-7.163 16-16-7.163-16-16-16zm0 28c-6.627 0-12-5.373-12-12s5.373-12 12-12 12 5.373 12 12-5.373 12-12 12z" fill="white"/>
            </svg>
          ),
          title: 'Completing Google Sign-up...',
          message: 'Please wait while we set up your account.',
          color: '#177508'
        };
      
      case 'success':
        return {
          icon: (
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="32" cy="32" r="32" fill="#177508"/>
              <path d="M20 32L28 40L44 24" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ),
          title: 'Account Created Successfully!',
          message: 'Your Google account has been linked. Redirecting...',
          color: '#177508'
        };
      
      case 'error':
        return {
          icon: (
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="32" cy="32" r="32" fill="#C9121A"/>
              <path d="M20 20L44 44M44 20L20 44" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ),
          title: 'Sign-up Failed',
          message: error,
          color: '#C9121A'
        };
    }
  };

  const content = getStatusContent();

  return (
    <div className='verification-container'>
      <div className='verification-content'>
        <div className='verification-card'>
          <div className='verification-icon'>
            {content.icon}
          </div>
          
          <h1 className='titles large color black'>{content.title}</h1>
          
          <p className='paragraphs medium color charcoal'>
            {content.message}
          </p>
          
          {status === 'error' && (
            <div className='verification-actions'>
              <button 
                className='btn primary'
                onClick={() => window.location.href = '/sign-up'}
              >
                Try Again
              </button>
              <button 
                className='btn secondary'
                onClick={() => navigate('/')}
              >
                Go to Home
              </button>
            </div>
          )}
          
          {status === 'loading' && (
            <div className='loading-spinner'>
              <div className='spinner'></div>
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default GoogleCallback; 